/**
 * API-Client für /api/shifts.
 */

import { apiFetch } from './api';

export type ShiftStatus = 'planned' | 'done' | 'cancelled';

export interface ApiShift {
  id: string;
  userId: string;
  locationId: string;
  templateId: string | null;
  shiftDate: string;     // YYYY-MM-DD
  startTime: string;     // HH:MM
  endTime: string;       // HH:MM
  status: ShiftStatus;
  note: string | null;
  /**
   * `null`, wenn der Aufrufer für eine FREMDE Schicht keine Lohn-Sichtbarkeit
   * hat (Phase G, Entscheidung #6 — Filialleiter ohne `managerCanSeeWages`
   * am Standort dieser Schicht). Für eigene Schichten immer eine Zahl.
   */
  hourlyWage: number | null;
  /** Pauschale Zusatzstunden (Fahrzeit, Aushilfe). Default 0. */
  bonusHours: number;
  /** Wenn true: Endzeit ist noch nicht eingetragen (z. B. "Ware"). */
  isOpenEnd: boolean;
  /** Phase F (Kapitel 11.2): planned = eingeplant, self_reported = Einspringen. */
  origin: 'planned' | 'self_reported';
  /**
   * Phase F (Kapitel 11.3/11.6): Status der BEANTRAGTEN Zusatzstunden.
   * Kommt als `'none'`, wenn der Aufrufer für diesen Standort kein
   * `managerCanSeeBonusHours` hat und die Schicht nicht die eigene ist —
   * die ganze Zusatzstunden-Seite ist dann serverseitig unsichtbar gemacht.
   */
  bonusStatus: 'none' | 'pending' | 'approved' | 'rejected';
  /** Ursprünglich beantragte Zahl (bleibt bei Ablehnung als Audit-Spur). */
  requestedBonusHours: number | null;
  bonusDecidedBy: string | null;
  bonusDecidedAt: string | null;
  /** Phase D/F (Kapitel 5.2/11.7): UUID der Person, die trotz genehmigter Abwesenheit eingetragen hat. */
  vacationOverrideBy: string | null;
  /** Abrechnung: Zeitpunkt der Abrechnung (ISO) oder null wenn offen. */
  settledAt: string | null;
  /** UUID des Admins/Managers, der abgerechnet hat (Audit) oder null. */
  settledBy: string | null;
  /** Nachtrag 11.09.2026: Tagesgehalt selbst aus der Kasse entnommen (dann auch settledAt gesetzt). */
  dailyPayoutAt?: string | null;
  /** Entnommener Betrag; null, wenn für den Aufrufer nicht sichtbar. */
  dailyPayoutAmount?: number | null;
  /**
   * Phase M (Kapitel 18, Entscheidung #21): Audit der flexiblen
   * Übergabezeit-Anpassung. `timeAdjustedBy`/`timeAdjustedAt` = wer hat
   * zuletzt Start- oder Endzeit verschoben. `originalStartTime`/
   * `originalEndTime` = einmaliger Snapshot der zuerst geplanten Zeit, bleibt
   * bei mehrfachem Anpassen unverändert. Alle vier `null` = noch nie angepasst.
   */
  timeAdjustedBy: string | null;
  timeAdjustedAt: string | null;
  originalStartTime: string | null;
  originalEndTime: string | null;
  clientUuid: string | null;
  createdAt: string;
  lastModified: string;
  /**
   * Phase DD (Kapitel 19.10, Entscheidung #39): UUID der Person, deren
   * Änderung den aktuellen `lastModified`-Stand erzeugt hat. Wird im
   * ConflictDialog (WeeklyPlanPage) angezeigt, um zu erklären, wer die
   * Zelle zuletzt geändert hat, seit der lokale Offline-Stand geladen wurde.
   */
  lastModifiedBy: string | null;
  deletedAt: string | null;
}

export interface ListShiftsParams {
  from?: string;
  to?: string;
  month?: string;        // YYYY-MM
  userId?: string;       // Admin-only
  status?: ShiftStatus;
  /** Nachtrag (Standort-Detail-Ansicht) — auf einen Standort einschränken. */
  locationId?: string;
  origin?: 'planned' | 'self_reported';
  bonusStatus?: 'none' | 'pending' | 'approved' | 'rejected';
}

export interface CreateShiftInput {
  userId?: string;       // optional; Admin kann fremde User setzen
  locationId: string;
  templateId?: string | null;
  shiftDate: string;
  startTime: string;
  endTime: string;
  status?: ShiftStatus;
  note?: string | null;
  hourlyWage?: number;
  bonusHours?: number;
  isOpenEnd?: boolean;
  clientUuid?: string;
  /** Phase F (Kapitel 11.3/11.5): Einspringen mit Zusatzstunden-Antrag. */
  requestedBonusHours?: number;
  /** Phase D/F (Kapitel 5.2/11.7): Override-Bestätigung trotz genehmigter Abwesenheit. */
  vacationOverride?: boolean;
  /**
   * Phase DD (Kapitel 19.10, Entscheidung #39): true = die Baseline dieser
   * Zelle war beim Laden "leer" (Schichtplan-Veröffentlichen) — existiert
   * serverseitig bereits eine nicht gelöschte Schicht, antwortet der Server
   * mit 409 statt einer stillen Doppel-Anlage.
   */
  expectEmpty?: boolean;
}

export interface UpdateShiftInput {
  locationId?: string;
  templateId?: string | null;
  shiftDate?: string;
  startTime?: string;
  endTime?: string;
  status?: ShiftStatus;
  note?: string | null;
  hourlyWage?: number;
  bonusHours?: number;
  isOpenEnd?: boolean;
  /** Abrechnung: ISO-Zeitstempel zum Abrechnen, null zum Wieder-Öffnen. */
  settledAt?: string | null;
  /**
   * Gruppe F (Kapitel 19.10, Entscheidung #39/#42): true = dieses Update
   * überschreibt bewusst den Server-Stand einer als Konflikt gemeldeten Zelle
   * ("Übernehmen" im ConflictDialog) — löst die "Änderung überschrieben"-Push
   * an die ursprüngliche Person aus, falls die Schicht nicht die eigene ist.
   */
  conflictOverride?: boolean;
}

function buildQuery(params: ListShiftsParams): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export const shiftsApi = {
  list: (token: string, params: ListShiftsParams = {}) =>
    apiFetch<{ shifts: ApiShift[] }>(`/api/shifts${buildQuery(params)}`, { token }).then(
      (r) => r.shifts,
    ),

  get: (token: string, id: string) =>
    apiFetch<{ shift: ApiShift }>(`/api/shifts/${id}`, { token }).then((r) => r.shift),

  create: (token: string, data: CreateShiftInput) =>
    apiFetch<{ shift: ApiShift }>('/api/shifts', { method: 'POST', body: data, token }).then(
      (r) => r.shift,
    ),

  /**
   * Update einer Schicht.
   * @param expectedLastModified ISO-String des Server-Stands, auf dem die
   *   Mutation basiert. Wird als `X-Expected-Last-Modified`-Header
   *   mitgeschickt; Server gibt bei abweichender Version 409 zurück.
   */
  update: (
    token: string,
    id: string,
    data: UpdateShiftInput,
    expectedLastModified?: string,
  ) =>
    apiFetch<{ shift: ApiShift }>(`/api/shifts/${id}`, {
      method: 'PUT',
      body: data,
      token,
      headers: expectedLastModified
        ? { 'x-expected-last-modified': expectedLastModified }
        : undefined,
    }).then((r) => r.shift),

  remove: (token: string, id: string, expectedLastModified?: string) =>
    apiFetch<{ shift: ApiShift }>(`/api/shifts/${id}`, {
      method: 'DELETE',
      token,
      headers: expectedLastModified
        ? { 'x-expected-last-modified': expectedLastModified }
        : undefined,
    }).then((r) => r.shift),

  /**
   * Phase F (Kapitel 11.6): Zusatzstunden einer Einspringen-Schicht
   * bestätigen/ablehnen. Ausschließlich Chef-Rechte (serverseitig geprüft) —
   * bewusst kein Outbox-Eintrag, das ist eine Live-Entscheidung, keine
   * offline-fähige Schicht-Bearbeitung.
   */
  bonusDecision: (token: string, id: string, decision: 'approved' | 'rejected') =>
    apiFetch<{ shift: ApiShift }>(`/api/shifts/${id}/bonus-decision`, {
      method: 'POST',
      body: { decision },
      token,
    }).then((r) => r.shift),

  /**
   * Phase M (Kapitel 18, Entscheidung #21): Übergabezeit zwischen zwei
   * direkt aufeinanderfolgenden eigenen Schichten verschieben. Bewusst kein
   * Outbox-Eintrag — die Server-Validierung (Nachbar-Schicht,
   * Öffnungszeiten) braucht sofortiges Feedback, keine Offline-Warteschlange.
   */
  /**
   * Nachtrag 11.09.2026: Tagesgehalt der eigenen Schicht aus der Kasse
   * entnehmen. Betrag rechnet der Server; die Schicht ist danach abgerechnet.
   * Live-Aufruf, kein Outbox-Eintrag (braucht sofortige Rückmeldung).
   */
  dailyPayout: (token: string, id: string) =>
    apiFetch<{ shift: ApiShift }>(`/api/shifts/${id}/daily-payout`, { method: 'POST', token }).then(
      (r) => r.shift,
    ),

  adjustHandoverTime: (token: string, id: string, field: 'start' | 'end', time: string) =>
    apiFetch<{ shift: ApiShift }>(`/api/shifts/${id}/handover-time`, {
      method: 'POST',
      body: { field, time },
      token,
    }).then((r) => r.shift),

  /**
   * Gruppe F (Kapitel 19.9b, Entscheidung #42): einmal NACH dem
   * Veröffentlichen-Durchlauf aufgerufen — der Server prüft serverseitig, ob
   * für diesen Standort/Zeitraum noch offene Tage ohne Schicht bestehen, und
   * schickt bei Bedarf die "Unterbesetzt veröffentlicht"-Push an die
   * Filialleitung. Rein informativ, kein Outbox-Eintrag nötig.
   */
  notifyPublishGaps: (token: string, locationId: string, from: string, to: string) =>
    apiFetch<{ gapCount: number }>('/api/shifts/notify-publish-gaps', {
      method: 'POST',
      body: { locationId, from, to },
      token,
    }).then((r) => r.gapCount),
};
